import type { AudioClip, Project } from '@/domain/types';
import { clipEndTicks, clipLengthTicks, effectivePlaybackRate, tempoMapOf } from '@/domain/selectors';
import { ticksToSeconds } from '@/timing/tempoMap';
import { readEnvelopeBetween } from '@/assets/waveform';
import type { WaveformPeaks } from '@/assets/waveformTypes';
import { automationValueAt } from '@/engine/automation';
import { findCrossfades } from '@/domain/crossfade';
import { bareOverlapsOnLane } from '@/domain/overlap';
import { fadeGainAt } from '@/engine/schedulePlan';
import { clipGainAt, clipMapsEvenly, formatRideDb, sortedGainPoints } from '@/domain/clipGain';
import { compSources, trackTakeName } from '@/domain/takes';
import type { ClipRect, LaneLayout } from './view';
import { takeLaneCount } from './view';
import {
  automationValueToY,
  clipGainArea,
  clipGainToY,
  clipSourceToX,
  gridLines,
  laneLayout,
  meterMarkers,
  tempoMarkers,
  tickToX,
  visibleClipRects,
  xToClipSource,
  xToTick,
  type TimelineView,
} from './view';

/**
 * Canvas rendering for the arrangement.
 *
 * Everything in the arrangement — grid, lanes, clips, waveforms, fades,
 * selection — is drawn onto a single canvas. Using DOM nodes per clip would
 * mean thousands of elements and a layout pass on every drag; one canvas means
 * one paint, and the cost scales with the size of the window rather than with
 * the size of the project.
 */

export interface DrawContext {
  ctx: CanvasRenderingContext2D;
  project: Project;
  view: TimelineView;
  selectedClipIds: ReadonlySet<string>;
  selectedTrackId: string | null;
  selectedPointIds: ReadonlySet<string>;
  peaksFor: (assetId: string) => WaveformPeaks | null;
  marquee: { x0: number; y0: number; x1: number; y1: number } | null;
  /** The range being comped in, while a comp drag is under way. */
  compRange: { lane: LaneLayout; fromX: number; toX: number } | null;
  /** The stretch of time selected for splicing, inserting and cropping. */
  timeRange: { startTicks: number; endTicks: number; trackIds: readonly string[] } | null;
  /** While on, every audio clip shows its gain envelope and its handles. */
  clipGainEditing: boolean;
  /** The ride point under the pointer, whose level is worth reading out. */
  draggingGainPointId: string | null;
  dpr: number;
}

const COLORS = {
  bg: '#08090b',
  laneEven: '#0b0d10',
  laneOdd: '#0a0c0f',
  laneSelected: '#0e1116',
  gridBar: '#232932',
  gridBeat: '#171b21',
  gridSub: '#111419',
  laneBorder: '#181c22',
  loop: 'rgba(79, 143, 247, 0.10)',
  loopEdge: 'rgba(79, 143, 247, 0.55)',
  clipText: 'rgba(255,255,255,0.82)',
  selection: '#7fb0ff',
  marquee: 'rgba(79, 143, 247, 0.16)',
  marqueeEdge: 'rgba(127, 176, 255, 0.8)',
  marker: '#e0a13c',
  automationBg: '#0a0d11',
  automationGrid: '#151a20',
  automationLine: '#5fa4ff',
  automationFill: 'rgba(79, 143, 247, 0.12)',
  automationPoint: '#bcd6ff',
  automationDisabled: '#3d4550',
  takeLane: '#080a0d',
  compRange: 'rgba(79, 143, 247, 0.22)',
  compHeard: 'rgba(127, 176, 255, 0.9)',
  clipGain: 'rgba(126, 224, 179, 0.85)',
  crossfade: 'rgba(255, 255, 255, 0.07)',
  crossfadeLine: 'rgba(255, 255, 255, 0.55)',
  // Warm rather than red: this is something to look at, not an error. It has to
  // read at a glance against a waveform without shouting over one.
  bareOverlap: 'rgba(232, 158, 74, 0.20)',
  bareOverlapLine: 'rgba(232, 158, 74, 0.85)',
  frozen: 'rgba(120, 170, 255, 0.05)',
  frozenEdge: 'rgba(120, 170, 255, 0.35)',
  timeRange: 'rgba(255, 255, 255, 0.09)',
  timeRangeEdge: 'rgba(230, 238, 248, 0.75)',
};

export function drawArrangement(dc: DrawContext): void {
  const { ctx, view } = dc;
  ctx.save();
  ctx.scale(dc.dpr, dc.dpr);
  ctx.clearRect(0, 0, view.width, view.height);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, view.width, view.height);

  drawLanes(dc);
  drawGrid(dc);
  drawLoopRegion(dc);
  drawClips(dc);
  drawCrossfades(dc);
  drawBareOverlaps(dc);
  drawAutomation(dc);
  drawCompRange(dc);
  drawTimeRange(dc);
  drawMarquee(dc);

  ctx.restore();
}

function drawLanes(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  const { lanes } = laneLayout(project);
  let trackIndex = -1;
  lanes.forEach((lane) => {
    if (lane.laneId === null && lane.takeLane === null) trackIndex++;
    const y = lane.top - view.scrollTop;
    if (y + lane.height < 0 || y > view.height) return;

    // Automation and take lanes sit visually inside their track: darker, so the
    // eye reads them as belonging to the lane above rather than as more tracks.
    ctx.fillStyle =
      lane.laneId !== null
        ? COLORS.automationBg
        : lane.takeLane !== null
          ? COLORS.takeLane
          : lane.trackId === dc.selectedTrackId
            ? COLORS.laneSelected
            : trackIndex % 2 === 0
              ? COLORS.laneEven
              : COLORS.laneOdd;
    ctx.fillRect(0, y, view.width, lane.height);

    // A frozen track is tinted so it is obvious at a glance why its clips no
    // longer respond to editing in the way they used to sound.
    if (lane.laneId === null && lane.takeLane === null && project.tracks[lane.trackId]?.freeze) {
      ctx.fillStyle = COLORS.frozen;
      ctx.fillRect(0, y, view.width, lane.height);
    }

    ctx.fillStyle = COLORS.laneBorder;
    ctx.fillRect(0, y + lane.height - 1, view.width, 1);
  });
}

function drawGrid(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  const lines = gridLines(project, view);
  for (const line of lines) {
    if (line.x < -1 || line.x > view.width + 1) continue;
    ctx.fillStyle = line.kind === 'bar' ? COLORS.gridBar : line.kind === 'beat' ? COLORS.gridBeat : COLORS.gridSub;
    ctx.fillRect(Math.round(line.x) + 0.5, 0, 1, view.height);
  }
}

function drawLoopRegion(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  if (!project.transport.loopEnabled) return;
  const x0 = tickToX(view, project.transport.loopStartTicks);
  const x1 = tickToX(view, project.transport.loopEndTicks);
  if (x1 < 0 || x0 > view.width) return;
  ctx.fillStyle = COLORS.loop;
  ctx.fillRect(x0, 0, x1 - x0, view.height);
  ctx.fillStyle = COLORS.loopEdge;
  ctx.fillRect(Math.round(x0), 0, 1, view.height);
  ctx.fillRect(Math.round(x1) - 1, 0, 1, view.height);
}

function drawClips(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  const { lanes } = laneLayout(project);
  const rects = visibleClipRects(project, view, lanes);

  // Which take each comp clip came from, per track, worked out once per
  // frame: it is what a comp view is *for*, and it is derived rather than
  // stored, so it costs a comparison per comp clip per take.
  const sourcesByTrack = new Map<string, Map<string, number | null>>();
  const sourcesFor = (trackId: string): Map<string, number | null> => {
    let sources = sourcesByTrack.get(trackId);
    if (!sources) {
      sources = new Map(compSources(project, trackId).map((s) => [s.clipId, s.takeLane]));
      sourcesByTrack.set(trackId, sources);
    }
    return sources;
  };

  for (const rect of rects) {
    const clip = project.clips[rect.clipId];
    const track = project.tracks[rect.trackId];
    if (!clip || !track) continue;

    const selected = dc.selectedClipIds.has(clip.id);
    const base = clip.color ?? track.color;
    // A take is an alternative, not what you are hearing, so it is drawn
    // quieter than the comp above it.
    const isTake = rect.takeLane > 0;
    const x = Math.max(-2, rect.x);
    const right = Math.min(view.width + 2, rect.x + rect.width);
    const width = right - x;
    if (width <= 0) continue;

    const y = rect.y + 2;
    const height = rect.height - 5;
    const headerH = Math.min(15, Math.max(11, height * 0.22));

    ctx.save();
    roundRect(ctx, x, y, width, height, 2);
    ctx.clip();

    // Body
    ctx.fillStyle = withAlpha(base, clip.muted ? 0.1 : isTake ? 0.11 : 0.18);
    ctx.fillRect(x, y, width, height);
    // Header strip
    ctx.fillStyle = withAlpha(base, clip.muted ? 0.24 : isTake ? 0.3 : 0.55);
    ctx.fillRect(x, y, width, headerH);

    if (clip.kind === 'audio') {
      drawWaveform(dc, clip, rect, x, y + headerH, width, height - headerH, base, isTake ? 0.45 : 1);
      if (!isTake) drawFades(dc, clip, rect, y, height);
      if (!isTake) drawGainEnvelope(dc, clip, rect);
    } else {
      drawMidiPreview(dc, clip.id, x, y + headerH, width, height - headerH, base);
    }

    if (width > 26) {
      ctx.fillStyle = isTake ? 'rgba(255,255,255,0.5)' : COLORS.clipText;
      ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.textBaseline = 'middle';

      // A clip that is not playing at its recorded speed says so on its header:
      // it is otherwise invisible, and it changes what the audio is.
      const rate = clip.kind === 'audio' ? effectivePlaybackRate(project, clip) : 1;
      let badge = '';
      if (clip.kind === 'audio' && Math.abs(rate - 1) > 1e-3) {
        // A clip that follows the tempo is marked, because its speed is not a
        // property of the clip and will move again when the tempo does.
        const follows = clip.warpSourceBpm !== null ? '\u223c' : '';
        badge = `${follows}${Math.round((1 / rate) * 100)}%${clip.preservePitch ? '' : ' \u2933'}`;
      }
      // A comp clip says which take it is, so four lanes of takes can be
      // read against the comp above them without guessing by the waveform.
      if (!isTake && takeLaneCount(project, track.id) > 0) {
        const lane = sourcesFor(track.id).get(clip.id) ?? null;
        if (lane !== null) badge = `${trackTakeName(track, lane)}${badge ? ' · ' + badge : ''}`;
      }
      const badgeWidth = badge ? ctx.measureText(badge).width + 8 : 0;
      ctx.fillText(truncate(ctx, clip.name, width - 10 - badgeWidth), x + 5, y + headerH / 2 + 0.5);
      if (badge && width > badgeWidth + 30) {
        ctx.textAlign = 'right';
        ctx.fillStyle = isTake ? 'rgba(255,255,255,0.45)' : 'rgba(10,12,16,0.75)';
        ctx.fillText(badge, x + width - 5, y + headerH / 2 + 0.5);
        ctx.textAlign = 'left';
      }
    }
    ctx.restore();

    // Warp markers, drawn as ticks down the clip at the beat each one pins.
    if (clip.kind === 'audio' && clip.warpMarkers.length > 0 && !isTake) {
      const lengthTicks = clipLengthTicks(project, clip);
      for (const marker of clip.warpMarkers) {
        const at = clip.startTicks + marker.beat * project.transport.ppq;
        if (at <= clip.startTicks || at >= clip.startTicks + lengthTicks) continue;
        const mx = Math.round(tickToX(view, at)) + 0.5;
        if (mx < x || mx > x + width) continue;
        ctx.fillStyle = 'rgba(240, 214, 140, 0.85)';
        ctx.fillRect(mx, y, 1, height);
        ctx.fillRect(mx - 2, y, 5, 3);
      }
    }

    ctx.strokeStyle = selected ? COLORS.selection : withAlpha(base, isTake ? 0.35 : 0.7);
    ctx.lineWidth = selected ? 1.6 : 1;
    roundRect(ctx, x + 0.5, y + 0.5, width - 1, height - 1, 2);
    ctx.stroke();

    // The parts of a take that the comp is made of, lit up on the take itself:
    // the composite line, drawn where the choosing happens rather than only
    // where its result plays. A take that is not heard anywhere stays dim.
    if (isTake) {
      for (const [compId, lane] of sourcesFor(track.id)) {
        if (lane !== rect.takeLane) continue;
        const comp = project.clips[compId];
        if (!comp) continue;
        const from = Math.max(x, tickToX(view, comp.startTicks));
        const to = Math.min(x + width, tickToX(view, clipEndTicks(project, comp)));
        if (to <= from) continue;
        ctx.fillStyle = withAlpha(base, 0.22);
        ctx.fillRect(from, y, to - from, height);
        ctx.fillStyle = COLORS.compHeard;
        ctx.fillRect(from, y + height - 3, to - from, 3);
      }
    }
  }
}

function drawWaveform(
  dc: DrawContext,
  clip: AudioClip,
  rect: { x: number; width: number },
  x: number,
  y: number,
  width: number,
  height: number,
  color: string,
  opacity = 1,
): void {
  const { ctx } = dc;
  if (height < 6 || width < 2) return;

  const peaks = dc.peaksFor(clip.assetId);
  if (!peaks) {
    // Peaks are still being generated: a centre line is honest about "audio is
    // here, its shape is coming" without pretending to show a waveform.
    ctx.fillStyle = withAlpha(color, 0.22);
    ctx.fillRect(x, y + height / 2 - 0.5, width, 1);
    return;
  }

  // `rect` is the clip's full extent; `x`/`width` are the part on screen. Each
  // column is bounded by the moment of source heard at its two edges, so the
  // waveform is indexed from the clip's own offset and trimming slides the
  // drawn shape exactly as it slides the audio; a reversed clip is a descending
  // list of edges, drawn the way it is heard without a special case.
  const pixels = Math.ceil(width);
  const edges = new Float64Array(pixels + 1);
  if (clipMapsEvenly(dc.project, clip)) {
    // The common case is a straight line, and asking the mapping per column
    // for every clip on screen would be paying for the warp on clips that
    // have none.
    const sourceSecondsPerPixel = clip.durationSec / Math.max(1, rect.width);
    const clippedLeft = Math.max(0, x - rect.x);
    for (let p = 0; p <= pixels; p++) {
      const consumed = (clippedLeft + p) * sourceSecondsPerPixel;
      const sourceSec = clip.reversed ? clip.offsetSec + clip.durationSec - consumed : clip.offsetSec + consumed;
      edges[p] = sourceSec * peaks.sampleRate;
    }
  } else {
    // Warped, or lying under a tempo change: the source under a column is
    // wherever the scheduler says it is, which is the only way the ride drawn
    // on top can sit on the transient it was put on.
    for (let p = 0; p <= pixels; p++) {
      edges[p] = xToClipSource(dc.project, dc.view, clip, x + p) * peaks.sampleRate;
    }
  }

  const channels = Math.min(2, peaks.channelCount);
  const laneH = height / channels;
  const envelope = new Float32Array(pixels * 2);

  ctx.fillStyle = withAlpha(color, (clip.muted ? 0.3 : 0.92) * opacity);
  for (let c = 0; c < channels; c++) {
    readEnvelopeBetween(peaks, c, edges, pixels, envelope);
    const mid = y + laneH * c + laneH / 2;
    const scale = (laneH / 2) * 0.92;
    for (let p = 0; p < pixels; p++) {
      const top = mid - envelope[p * 2 + 1] * scale;
      const bottom = mid - envelope[p * 2] * scale;
      ctx.fillRect(x + p, top, 1, Math.max(1, bottom - top));
    }
  }
}

function drawFades(
  dc: DrawContext,
  clip: AudioClip,
  rect: { x: number; width: number },
  y: number,
  height: number,
): void {
  const { ctx, project } = dc;
  const map = tempoMapOf(project);
  const clipStartSec = ticksToSeconds(map, clip.startTicks);
  const clipEndSec = ticksToSeconds(map, clip.startTicks + clipLengthTicks(project, clip));
  const clipSec = clipEndSec - clipStartSec;
  if (clipSec <= 0) return;

  const pxPerSec = rect.width / clipSec;

  // The masked-out area is drawn under the fade's real curve rather than as a
  // triangle, so what is on screen is the shape that will be heard — which is
  // the entire point of being able to bend it.
  for (const rising of [true, false]) {
    const fade = rising ? clip.fadeIn : clip.fadeOut;
    if (fade.durationSec <= 0) continue;
    const width = Math.min(rect.width, fade.durationSec * pxPerSec);
    if (width < 1) continue;
    const originX = rising ? rect.x : rect.x + rect.width;
    const direction = rising ? 1 : -1;

    const steps = Math.max(2, Math.min(80, Math.round(width)));
    ctx.beginPath();
    ctx.moveTo(originX, y);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const gain = fadeGainAt(fade.curve, t, 1, true, fade.curvature);
      ctx.lineTo(originX + direction * width * t, y + height - gain * height);
    }
    ctx.lineTo(originX + direction * width, y);
    ctx.closePath();
    ctx.fillStyle = 'rgba(8, 9, 11, 0.62)';
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const gain = fadeGainAt(fade.curve, t, 1, true, fade.curvature);
      const px = originX + direction * width * t;
      const py = y + height - gain * height;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
}

/**
 * A clip's own gain envelope, over its waveform.
 *
 * Drawn only when there is one, or while the envelope tool is on — an empty
 * envelope is a flat line through the middle of every clip in the project, which
 * is visual noise standing in for nothing. While the tool is on the line appears
 * anyway, at unity, so there is something to aim at.
 */
function drawGainEnvelope(dc: DrawContext, clip: AudioClip, rect: ClipRect): void {
  const { ctx } = dc;
  const points = sortedGainPoints(clip.gainEnvelope);
  if (points.length === 0 && !dc.clipGainEditing) return;
  if (rect.width < 6) return;

  const area = clipGainArea(rect);
  if (area.height < 8) return;

  const left = Math.max(rect.x, -2);
  const right = Math.min(rect.x + rect.width, dc.view.width + 2);
  if (right <= left) return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(left, area.top, right - left, area.height);
  ctx.clip();

  // Unity, so a ride can be read against the level it departs from.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(left, clipGainToY(area, 1));
  ctx.lineTo(right, clipGainToY(area, 1));
  ctx.stroke();

  // The curve itself, sampled so that a smooth segment is drawn as the shape it
  // will be heard as rather than as a straight line between its ends.
  ctx.strokeStyle = COLORS.clipGain;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  const steps = Math.max(2, Math.min(200, Math.round(right - left)));
  for (let i = 0; i <= steps; i++) {
    const px = left + ((right - left) * i) / steps;
    const gain = clipGainAt(points, xToClipSource(dc.project, dc.view, clip, px));
    const py = clipGainToY(area, gain);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();

  if (dc.clipGainEditing) {
    ctx.fillStyle = COLORS.clipGain;
    for (const point of points) {
      const px = clipSourceToX(dc.project, dc.view, clip, point.sourceSec);
      const py = clipGainToY(area, point.gain);
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Every level, in decibels, beside its point — or as many as fit. A ride
    // drawn by eye is the one control a mix engineer cannot trust; a number
    // beside it is what makes "about there" into "there". Labels that would
    // overlap the last one are skipped, so a dense recorded ride reads as a
    // sampling of its levels rather than as a smear. The point being dragged is
    // always labelled, because it is the one being decided.
    ctx.font = '10px ui-monospace, monospace';
    ctx.textBaseline = 'bottom';
    let lastLabelRight = -Infinity;
    for (const point of points) {
      const px = clipSourceToX(dc.project, dc.view, clip, point.sourceSec);
      const dragging = point.id === dc.draggingGainPointId;
      if (!dragging && px < lastLabelRight + 6) continue;
      const label = formatRideDb(point.gain);
      const width = ctx.measureText(label).width;
      const py = clipGainToY(area, point.gain);
      const textX = Math.min(right - width - 2, Math.max(left + 2, px + 5));
      const textY = Math.max(area.top + 11, py - 4);
      if (dragging) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(textX - 2, textY - 11, width + 4, 12);
      }
      ctx.fillStyle = dragging ? '#ffffff' : COLORS.clipGain;
      ctx.fillText(label, textX, textY);
      lastLabelRight = textX + width;
    }
  }

  ctx.restore();
}

function drawMidiPreview(
  dc: DrawContext,
  clipId: string,
  x: number,
  y: number,
  width: number,
  height: number,
  color: string,
): void {
  const { ctx, project, view } = dc;
  const clip = project.clips[clipId];
  if (!clip || clip.kind !== 'midi' || clip.notes.length === 0 || height < 6) return;

  let low = 127;
  let high = 0;
  for (const note of clip.notes) {
    if (note.pitch < low) low = note.pitch;
    if (note.pitch > high) high = note.pitch;
  }
  const span = Math.max(6, high - low + 1);
  const rowH = Math.max(1, height / span);
  const pxPerTick = view.pixelsPerBeat / view.ppq;
  const clipX = tickToX(view, clip.startTicks);

  ctx.fillStyle = withAlpha(color, 0.95);
  for (const note of clip.notes) {
    const nx = clipX + note.startTicks * pxPerTick;
    const nw = Math.max(1.5, note.durationTicks * pxPerTick);
    if (nx + nw < x || nx > x + width) continue;
    const ny = y + height - (note.pitch - low + 1) * rowH;
    ctx.fillRect(nx, ny, nw, Math.max(1, rowH - 0.5));
  }
}

/**
 * Automation curves.
 *
 * The curve is sampled per pixel rather than drawn point to point, so a smooth
 * or held segment looks the way it will sound. Sampling costs one evaluation
 * per pixel column, which is bounded by the width of the window.
 */
function drawAutomation(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  const { lanes } = laneLayout(project);

  for (const laneLayoutEntry of lanes) {
    if (laneLayoutEntry.laneId === null) continue;
    const y = laneLayoutEntry.top - view.scrollTop;
    if (y + laneLayoutEntry.height < 0 || y > view.height) continue;

    const lane = project.automation[laneLayoutEntry.laneId];
    if (!lane) continue;

    const track = project.tracks[laneLayoutEntry.trackId];
    const colour = lane.enabled ? (track?.color ?? COLORS.automationLine) : COLORS.automationDisabled;

    // Guide lines at the top, middle and bottom of the usable range.
    ctx.fillStyle = COLORS.automationGrid;
    for (const value of [0, 0.5, 1]) {
      ctx.fillRect(0, Math.round(automationValueToY(laneLayoutEntry, value, view.scrollTop)), view.width, 1);
    }

    if (lane.points.length === 0) continue;

    const path = new Path2D();
    const bottom = laneLayoutEntry.top - view.scrollTop + laneLayoutEntry.height - 1;
    let started = false;
    for (let x = 0; x <= view.width; x++) {
      const value = automationValueAt(lane, xToTick(view, x));
      if (value === null) continue;
      const py = automationValueToY(laneLayoutEntry, value, view.scrollTop);
      if (started) path.lineTo(x, py);
      else {
        path.moveTo(x, py);
        started = true;
      }
    }
    if (!started) continue;

    // A soft fill under the curve reads as "level" at a glance.
    const fill = new Path2D(path);
    fill.lineTo(view.width, bottom);
    fill.lineTo(0, bottom);
    fill.closePath();
    ctx.fillStyle = lane.enabled ? COLORS.automationFill : 'rgba(61, 69, 80, 0.1)';
    ctx.fill(fill);

    ctx.strokeStyle = colour;
    ctx.lineWidth = lane.enabled ? 1.5 : 1;
    ctx.stroke(path);

    for (const point of lane.points) {
      const px = tickToX(view, point.tick);
      if (px < -8 || px > view.width + 8) continue;
      const py = automationValueToY(laneLayoutEntry, point.value, view.scrollTop);
      const selected = dc.selectedPointIds.has(point.id);

      ctx.beginPath();
      ctx.arc(px, py, selected ? 4.5 : 3.2, 0, Math.PI * 2);
      ctx.fillStyle = selected ? COLORS.selection : COLORS.automationPoint;
      ctx.fill();

      // A held point is drawn square, so the curve type is visible without
      // selecting it.
      if (point.curve === 'hold') {
        ctx.fillStyle = COLORS.bg;
        ctx.fillRect(px - 1.2, py - 1.2, 2.4, 2.4);
      }
    }
  }
}

/**
 * The crossfades on each clip lane.
 *
 * They are drawn after the clips rather than inside them, because a crossfade
 * belongs to the *join* — it spans two clips, and drawing half of it on each
 * would leave the shape to be inferred from two separate triangles.
 */
function drawCrossfades(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  const { lanes } = laneLayout(project);

  for (const lane of lanes) {
    if (lane.laneId !== null) continue;
    const y = lane.top - view.scrollTop;
    if (y + lane.height < 0 || y > view.height) continue;

    for (const region of findCrossfades(project, lane.trackId, lane.takeLane ?? 0)) {
      const x0 = tickToX(view, region.startTicks);
      const x1 = tickToX(view, region.endTicks);
      if (x1 < 0 || x0 > view.width || x1 - x0 < 1) continue;

      const top = y + 2;
      const bottom = y + lane.height - 4;

      ctx.fillStyle = COLORS.crossfade;
      ctx.fillRect(x0, top, x1 - x0, bottom - top);

      // The two curves in their real shapes, including any bend, so the
      // picture matches the sound rather than being a decorative cross.
      const incoming = project.clips[region.incomingId];
      const fade = incoming?.kind === 'audio' ? incoming.fadeIn : null;
      const curve = fade?.curve ?? 'equalPower';
      const curvature = fade?.curvature ?? 0;

      ctx.strokeStyle = COLORS.crossfadeLine;
      ctx.lineWidth = 1;
      for (const rising of [false, true]) {
        ctx.beginPath();
        const steps = Math.max(2, Math.min(64, Math.round(x1 - x0)));
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const gain = fadeGainAt(curve, t, 1, rising, curvature);
          const px = x0 + (x1 - x0) * t;
          const py = bottom - (bottom - top) * gain;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      // A handle where the curves cross: the place the eye already looks, and
      // the place dragging changes the shape.
      const midX = (x0 + x1) / 2;
      const midY = bottom - (bottom - top) * fadeGainAt(curve, 0.5, 1, true, curvature);
      ctx.beginPath();
      ctx.arc(midX, midY, 3.4, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.crossfadeLine;
      ctx.fill();
    }
  }
}

/**
 * The overlaps nothing is covering.
 *
 * Two clips at full level in the same place is the one thing the timeline could
 * not previously show, and the reason it matters is that it is almost always an
 * accident — a drag that went a little too far. Drawn after the clips and after
 * the crossfades, in hatching rather than a fill, so it reads as a mark *on* the
 * material rather than as more material.
 */
function drawBareOverlaps(dc: DrawContext): void {
  const { ctx, project, view } = dc;
  const { lanes } = laneLayout(project);

  for (const lane of lanes) {
    if (lane.laneId !== null) continue;
    const y = lane.top - view.scrollTop;
    if (y + lane.height < 0 || y > view.height) continue;

    for (const overlap of bareOverlapsOnLane(project, lane.trackId, lane.takeLane ?? 0)) {
      const x0 = tickToX(view, overlap.bareStartTicks);
      const x1 = tickToX(view, overlap.bareEndTicks);
      if (x1 < 0 || x0 > view.width || x1 - x0 < 1) continue;

      const top = y + 2;
      const bottom = y + lane.height - 4;

      ctx.fillStyle = COLORS.bareOverlap;
      ctx.fillRect(x0, top, x1 - x0, bottom - top);

      // Diagonals, clipped to the region: a hatch says "these two are on top of
      // each other" in a way a flat tint cannot, and cannot be mistaken for a
      // clip of its own.
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, top, x1 - x0, bottom - top);
      ctx.clip();
      ctx.strokeStyle = COLORS.bareOverlapLine;
      ctx.lineWidth = 1;
      const height = bottom - top;
      for (let x = x0 - height; x < x1 + height; x += 7) {
        ctx.beginPath();
        ctx.moveTo(x, bottom);
        ctx.lineTo(x + height, top);
        ctx.stroke();
      }
      ctx.restore();

      ctx.strokeStyle = COLORS.bareOverlapLine;
      ctx.lineWidth = 1;
      ctx.strokeRect(x0 + 0.5, top + 0.5, Math.max(1, x1 - x0 - 1), bottom - top - 1);
    }
  }
}

/** The range a comp drag is about to promote. */
function drawCompRange(dc: DrawContext): void {
  const { ctx, view, compRange } = dc;
  if (!compRange) return;
  const x = Math.min(compRange.fromX, compRange.toX);
  const width = Math.abs(compRange.toX - compRange.fromX);
  const y = compRange.lane.top - view.scrollTop;

  ctx.fillStyle = COLORS.compRange;
  ctx.fillRect(x, y, width, compRange.lane.height);
  ctx.strokeStyle = COLORS.selection;
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, width, compRange.lane.height - 1);
}

/**
 * The selected stretch of time.
 *
 * It is drawn over the clips rather than under them, and only on the tracks it
 * covers, because which tracks are included is exactly what decides what a
 * splice will do.
 */
function drawTimeRange(dc: DrawContext): void {
  const { ctx, project, view, timeRange } = dc;
  if (!timeRange) return;

  const x0 = tickToX(view, timeRange.startTicks);
  const x1 = tickToX(view, timeRange.endTicks);
  if (x1 < 0 || x0 > view.width) return;

  const included = new Set(timeRange.trackIds);
  for (const lane of laneLayout(project).lanes) {
    if (!included.has(lane.trackId)) continue;
    const y = lane.top - view.scrollTop;
    if (y + lane.height < 0 || y > view.height) continue;
    ctx.fillStyle = COLORS.timeRange;
    ctx.fillRect(x0, y, x1 - x0, lane.height);
  }

  ctx.fillStyle = COLORS.timeRangeEdge;
  ctx.fillRect(Math.round(x0), 0, 1, view.height);
  ctx.fillRect(Math.round(x1) - 1, 0, 1, view.height);
}

function drawMarquee(dc: DrawContext): void {
  const { ctx, marquee } = dc;
  if (!marquee) return;
  const x = Math.min(marquee.x0, marquee.x1);
  const y = Math.min(marquee.y0, marquee.y1);
  const w = Math.abs(marquee.x1 - marquee.x0);
  const h = Math.abs(marquee.y1 - marquee.y0);
  ctx.fillStyle = COLORS.marquee;
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = COLORS.marqueeEdge;
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w, h);
}

// -- ruler -----------------------------------------------------------------

export function drawRuler(
  ctx: CanvasRenderingContext2D,
  project: Project,
  view: TimelineView,
  dpr: number,
): void {
  ctx.save();
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, view.width, view.height);
  ctx.fillStyle = '#0d0f13';
  ctx.fillRect(0, 0, view.width, view.height);

  if (project.transport.loopEnabled) {
    const x0 = tickToX(view, project.transport.loopStartTicks);
    const x1 = tickToX(view, project.transport.loopEndTicks);
    ctx.fillStyle = 'rgba(79, 143, 247, 0.28)';
    ctx.fillRect(x0, 0, x1 - x0, 4);
  }

  ctx.font = '10px ui-monospace, monospace';
  ctx.textBaseline = 'alphabetic';
  for (const line of gridLines(project, view)) {
    if (line.x < -20 || line.x > view.width + 20) continue;
    if (line.kind === 'bar') {
      ctx.fillStyle = '#2b323c';
      ctx.fillRect(Math.round(line.x) + 0.5, view.height - 11, 1, 11);
      if (line.label) {
        ctx.fillStyle = '#7d8794';
        ctx.fillText(line.label, Math.round(line.x) + 4, view.height - 14);
      }
    } else if (line.kind === 'beat') {
      ctx.fillStyle = '#1e232b';
      ctx.fillRect(Math.round(line.x) + 0.5, view.height - 5, 1, 5);
    }
  }

  // Meter changes, so a project whose bars change length says so on the ruler
  // rather than only in the transport when the playhead happens to be there.
  // Skipped at tick 0, where the transport already shows the project meter.
  for (const meter of meterMarkers(project, view)) {
    if (meter.tick <= 0 || meter.x < -30 || meter.x > view.width + 30) continue;
    ctx.fillStyle = '#39424f';
    ctx.fillRect(Math.round(meter.x) + 0.5, 0, 1, view.height);
    ctx.fillStyle = '#9aa6b4';
    ctx.fillText(meter.label, Math.round(meter.x) + 4, 10);
  }

  // Tempo changes share the ruler's top band with the meter labels, but sit on
  // the other side of their line: a bar where both change at once stays legible.
  ctx.textAlign = 'right';
  for (const tempo of tempoMarkers(project, view)) {
    if (tempo.x < -40 || tempo.x > view.width + 40) continue;
    ctx.fillStyle = '#4a5566';
    ctx.fillRect(Math.round(tempo.x) + 0.5, 0, 1, view.height);
    ctx.fillStyle = '#8fa4c0';
    ctx.fillText(`${tempo.ramps ? '\u2197' : ''}${tempo.label}`, Math.round(tempo.x) - 4, 10);
  }
  ctx.textAlign = 'left';

  for (const marker of project.markers) {
    const x = tickToX(view, marker.tick);
    if (x < -40 || x > view.width + 40) continue;
    ctx.fillStyle = COLORS.marker;
    ctx.beginPath();
    ctx.moveTo(x, 4);
    ctx.lineTo(x + 7, 8);
    ctx.lineTo(x, 12);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

// -- helpers ---------------------------------------------------------------

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let result = text;
  while (result.length > 1 && ctx.measureText(`${result}…`).width > maxWidth) result = result.slice(0, -1);
  return `${result}…`;
}
